/**
 * Per-follow "seen" snapshot stored under `data.watchlistSeen` on a `savedCompany#*`
 * UserState item. It is the baseline for the Watchlist's "since your last visit" deltas:
 * open roles newer than `at` (and not in `roleIds`) count as new, and ids in `roleIds`
 * that are no longer open count as closed.
 *
 * `at` is epoch milliseconds (the jobs index filters `postedDate` in ms — storing ISO like
 * `followedAt` would force conversions at every call site). `roleIds` is capped so a
 * pathological company can't bloat the UserState item.
 */
export interface WatchlistSeenSnapshot {
  at: number;
  roleIds: string[];
}

export const WATCHLIST_SEEN_MAX_ROLE_IDS = 50;

export function parseWatchlistSeen(value: unknown): WatchlistSeenSnapshot | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.at !== 'number' || !Number.isFinite(record.at) || record.at <= 0) return null;
  if (!Array.isArray(record.roleIds)) return null;
  const roleIds = record.roleIds
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .slice(0, WATCHLIST_SEEN_MAX_ROLE_IDS);
  return { at: record.at, roleIds };
}

export function buildWatchlistSeen(roleIds: string[], now: number): WatchlistSeenSnapshot {
  return {
    at: now,
    roleIds: roleIds.filter((id) => id.length > 0).slice(0, WATCHLIST_SEEN_MAX_ROLE_IDS),
  };
}

/** Same role-id set (order-insensitive) — used to skip snapshot writes when nothing changed. */
export function watchlistSeenRoleIdsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((id) => setA.has(id));
}
